import React from 'react';
import { buildSectorRotation, QUADRANTS, RS_WINDOW, MOMENTUM_WINDOW, TRAIL_LENGTH } from '../shared/sectorRotation';
import {
  HIGHLIGHT_COLORS, GRID_INK, AXIS_INK, INK, MUTED, shortDate, useSectorHistory, usePinnedSlots, SectorPinPills, LoadingNote
} from './sectorChartShared';

/**
 * Sector rotation map (issue #365): x = a sector's 20-session return against the
 * all-sector average, y = how that lead changed over the last 5 sessions, with
 * each sector's recent path as a tail (a straight from-to line until pinned or hovered).
 *
 * DESCRIPTIVE, never a forecast -- issue #363 found neither axis ranks the next
 * days' returns, and the footnote says so. Every sector is direct-labelled and
 * listed as text below, so identity never rides on colour; pinned sectors take
 * the same highlight slots as the "Over time" chart.
 *
 * Hand-drawn SVG at the container's real pixel width (a scaled viewBox would
 * shrink the labels to ~6px on a phone).
 */

// 3M is the "Over time" tab's default window too, so the two tabs share one request.
const HISTORY_DAYS = 90;
const UNPINNED_DOT = '#6b7280';
const MARGIN = { top: 14, right: 16, bottom: 34, left: 44 };
const LABEL_HEIGHT = 13;

const SHORT_NAMES = {
  Communications: 'Comms',
  'Consumer Discretionary': 'Discretionary',
  'Consumer Staples': 'Staples',
  'Health Care': 'Health',
  Technology: 'Tech',
};
const shortName = (sector) => SHORT_NAMES[sector] || sector;

const fmtPts = (v) => `${v > 0 ? '+' : ''}${v.toFixed(1)}`;

// A callback ref held in state: the plot's container mounts only once data has
// arrived, which a mount-time `useRef` effect would never see.
const useContainerWidth = () => {
  const [node, setNode] = React.useState(null);
  const [width, setWidth] = React.useState(0);
  React.useEffect(() => {
    if (!node) return undefined;
    const measure = () => setWidth(node.clientWidth);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);
  return [setNode, width];
};

/** Round tick step (1 / 2 / 5 × 10^n) giving roughly `count` ticks per side. */
const niceStep = (max, count = 3) => {
  const raw = max / count;
  const pow = 10 ** Math.floor(Math.log10(raw));
  const unit = raw / pow;
  return (unit <= 1 ? 1 : unit <= 2 ? 2 : unit <= 5 ? 5 : 10) * pow;
};
const ticksFor = (max) => {
  const step = niceStep(max);
  const out = [];
  for (let v = step; v <= max; v += step) out.push(-v, v);
  return out;
};

/** Symmetric domain over every drawn position (tails included), so 0 sits dead centre. */
const domainOf = (points, key) =>
  Math.max(1, ...points.flatMap((p) => p.trail.map((t) => Math.abs(t[key])))) * 1.12;

/**
 * Each label takes the first of right / left / above / below its dot that
 * overprints neither another label nor another dot, and stays inside the plot.
 * Only when all four are taken does it fall back to the right and slide down.
 */
const placeLabels = (dots, plot) => {
  const GAP = 9;
  const overlaps = (a, b) => a.x0 < b.x1 && b.x0 < a.x1 && a.y0 < b.y1 && b.y0 < a.y1;
  const dotBoxes = dots.map((d) => ({ sector: d.sector, x0: d.cx - 7, x1: d.cx + 7, y0: d.cy - 7, y1: d.cy + 7 }));
  const placed = [];
  // most crowded first would be ideal; top-down is stable and good enough for eleven
  for (const d of [...dots].sort((p, q) => p.cy - q.cy)) {
    const width = d.label.length * 6.2 + 4;
    const candidates = [
      { anchor: 'start', lx: d.cx + GAP, ly: d.cy + 4 },
      { anchor: 'end', lx: d.cx - GAP, ly: d.cy + 4 },
      { anchor: 'middle', lx: d.cx, ly: d.cy - GAP - 2 },
      { anchor: 'middle', lx: d.cx, ly: d.cy + GAP + LABEL_HEIGHT - 3 },
    ].map((c) => {
      const x0 = c.anchor === 'start' ? c.lx : c.anchor === 'end' ? c.lx - width : c.lx - width / 2;
      return { ...c, x0, x1: x0 + width, y0: c.ly - LABEL_HEIGHT + 3, y1: c.ly + 3 };
    });
    const free = (c) => c.x0 >= plot.left && c.x1 <= plot.right && c.y0 >= plot.top && c.y1 <= plot.bottom
      && !placed.some((l) => overlaps(c, l))
      && !dotBoxes.some((box) => box.sector !== d.sector && overlaps(c, box));
    let choice = candidates.find(free);
    if (!choice) {
      choice = { ...candidates[0] };
      while (placed.some((l) => overlaps(choice, l))) { choice.ly += LABEL_HEIGHT; choice.y0 += LABEL_HEIGHT; choice.y1 += LABEL_HEIGHT; }
    }
    placed.push({ ...choice, sector: d.sector, label: d.label });
  }
  return placed;
};

const CORNERS = [
  { key: 'improving', x: 'left', y: 'top' },
  { key: 'leading', x: 'right', y: 'top' },
  { key: 'lagging', x: 'left', y: 'bottom' },
  { key: 'weakening', x: 'right', y: 'bottom' },
];

const Plot = ({ points, width, colorFor, hovered, setHovered }) => {
  const height = Math.max(260, Math.min(400, Math.round(width * 0.6)));
  const innerW = width - MARGIN.left - MARGIN.right;
  const innerH = height - MARGIN.top - MARGIN.bottom;
  const xMax = domainOf(points, 'rs');
  const yMax = domainOf(points, 'momentum');
  const x = (v) => MARGIN.left + ((v + xMax) / (2 * xMax)) * innerW;
  const y = (v) => MARGIN.top + ((yMax - v) / (2 * yMax)) * innerH;

  // pinned and hovered sectors draw last, so they sit on top
  const weight = (p) => (p.sector === hovered ? 2 : colorFor(p.sector) !== UNPINNED_DOT ? 1 : 0);
  const ordered = [...points].sort((a, b) => weight(a) - weight(b));
  const labels = placeLabels(
    ordered.map((p) => ({ sector: p.sector, label: shortName(p.sector), cx: x(p.rs), cy: y(p.momentum) })),
    { left: MARGIN.left, right: MARGIN.left + innerW, top: MARGIN.top, bottom: MARGIN.top + innerH }
  );

  return (
    <svg width={width} height={height} role="img" aria-label="Sector rotation map: relative strength against its recent change, per sector">
      {ticksFor(xMax).map((v) => (
        <g key={`x${v}`}>
          <line x1={x(v)} x2={x(v)} y1={MARGIN.top} y2={MARGIN.top + innerH} stroke={GRID_INK} strokeWidth={1} />
          <text x={x(v)} y={MARGIN.top + innerH + 13} fontSize={10} fill={AXIS_INK} textAnchor="middle">{fmtPts(v)}</text>
        </g>
      ))}
      {ticksFor(yMax).map((v) => (
        <g key={`y${v}`}>
          <line x1={MARGIN.left} x2={MARGIN.left + innerW} y1={y(v)} y2={y(v)} stroke={GRID_INK} strokeWidth={1} />
          <text x={MARGIN.left - 6} y={y(v) + 3} fontSize={10} fill={AXIS_INK} textAnchor="end">{fmtPts(v)}</text>
        </g>
      ))}
      {/* the two zero lines ARE the quadrant boundaries, so they are the strongest rules on the plot */}
      <line x1={x(0)} x2={x(0)} y1={MARGIN.top} y2={MARGIN.top + innerH} stroke={AXIS_INK} strokeWidth={1} />
      <line x1={MARGIN.left} x2={MARGIN.left + innerW} y1={y(0)} y2={y(0)} stroke={AXIS_INK} strokeWidth={1} />

      {CORNERS.map((c) => (
        <text
          key={c.key}
          x={c.x === 'left' ? MARGIN.left + 6 : MARGIN.left + innerW - 6}
          y={c.y === 'top' ? MARGIN.top + 12 : MARGIN.top + innerH - 6}
          textAnchor={c.x === 'left' ? 'start' : 'end'}
          fontSize={10}
          fontWeight={600}
          letterSpacing="0.06em"
          fill={MUTED}
        >
          {QUADRANTS[c.key].label.toUpperCase()}
        </text>
      ))}

      <text x={MARGIN.left + innerW / 2} y={height - 4} fontSize={10} fill={AXIS_INK} textAnchor="middle">
        ← behind · {RS_WINDOW}-session return vs the sector average (pts) · ahead →
      </text>
      <text transform={`translate(11 ${MARGIN.top + innerH / 2}) rotate(-90)`} fontSize={10} fill={AXIS_INK} textAnchor="middle">
        ← fading · {MOMENTUM_WINDOW}-session change · growing →
      </text>

      {ordered.map((p) => {
        const color = colorFor(p.sector);
        const dim = hovered && hovered !== p.sector;
        const emphasised = color !== UNPINNED_DOT || hovered === p.sector;
        return (
          <g key={p.sector} opacity={dim ? 0.3 : 1}>
            {/* eleven day-by-day paths are a scribble: a straight from-to line by default, the full path on demand */}
            <polyline
              points={(emphasised ? p.trail : [p.trail[0], p.trail[p.trail.length - 1]]).map((t) => `${x(t.rs)},${y(t.momentum)}`).join(' ')}
              fill="none"
              stroke={color}
              strokeWidth={emphasised ? 2 : 1.25}
              strokeOpacity={emphasised ? 0.75 : 0.4}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
            <circle cx={x(p.trail[0].rs)} cy={y(p.trail[0].momentum)} r={2} fill={color} fillOpacity={emphasised ? 0.75 : 0.4} />
            <circle cx={x(p.rs)} cy={y(p.momentum)} r={5} fill={color} stroke="#fff" strokeWidth={2} />
          </g>
        );
      })}

      {labels.map((l) => {
        const pinned = colorFor(l.sector) !== UNPINNED_DOT;
        return (
          <text
            key={l.sector}
            x={l.lx}
            y={l.ly}
            textAnchor={l.anchor}
            fontSize={11}
            fontWeight={pinned || hovered === l.sector ? 700 : 500}
            fill={INK}
            opacity={hovered && hovered !== l.sector ? 0.3 : 1}
            stroke="#fff"
            strokeWidth={3}
            paintOrder="stroke"
          >
            {l.label}
          </text>
        );
      })}

      {/* hit targets larger than the marks, on top of everything -- same order as the dots, so the topmost dot wins */}
      {ordered.map((p) => (
        <circle
          key={p.sector}
          cx={x(p.rs)}
          cy={y(p.momentum)}
          r={13}
          fill="transparent"
          style={{ cursor: 'pointer' }}
          onMouseEnter={() => setHovered(p.sector)}
          onMouseLeave={() => setHovered(null)}
        />
      ))}
    </svg>
  );
};

/** The map as text: every sector under its quadrant, with both values. Doubles as the hover readout. */
const QuadrantTable = ({ points, colorFor, hovered, setHovered }) => (
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '10px 16px', marginTop: '10px' }}>
    {['leading', 'weakening', 'lagging', 'improving'].map((key) => {
      const members = points.filter((p) => p.quadrant === key).sort((a, b) => b.rs - a.rs);
      return (
        <div key={key}>
          <div style={{ fontSize: '0.72rem', fontWeight: 600, color: INK }}>
            {QUADRANTS[key].label} <span style={{ fontWeight: 400, color: '#6b7280' }}>· {QUADRANTS[key].blurb}</span>
          </div>
          {members.length === 0 && <div style={{ fontSize: '0.7rem', color: MUTED, lineHeight: 1.7 }}>none</div>}
          {members.map((p) => (
            <div
              key={p.sector}
              onMouseEnter={() => setHovered(p.sector)}
              onMouseLeave={() => setHovered(null)}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: '8px',
                fontSize: '0.7rem',
                lineHeight: 1.7,
                color: '#374151',
                fontWeight: hovered === p.sector ? 600 : 400,
                cursor: 'default'
              }}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: colorFor(p.sector), display: 'inline-block' }} />
                {p.sector}
              </span>
              <span style={{ fontVariantNumeric: 'tabular-nums', color: '#6b7280' }}>
                {fmtPts(p.rs)} <span style={{ color: MUTED }}>({fmtPts(p.momentum)})</span>
              </span>
            </div>
          ))}
        </div>
      );
    })}
  </div>
);

const SectorRotationMap = () => {
  const { data, isLoading, isError } = useSectorHistory(HISTORY_DAYS);
  const pins = usePinnedSlots();
  const [hovered, setHovered] = React.useState(null);
  const [containerRef, width] = useContainerWidth();

  const rotation = React.useMemo(() => (data ? buildSectorRotation(data.performance) : null), [data]);
  const colorFor = (sector) => {
    const slot = pins.slotOf(sector);
    return slot === -1 ? UNPINNED_DOT : HIGHLIGHT_COLORS[slot];
  };

  if (isLoading) {
    return <LoadingNote>Loading sector rotation…</LoadingNote>;
  }
  if (isError || !rotation) return null;

  const ready = rotation.points.length > 0;
  const sectors = rotation.points.map((p) => p.sector).sort();

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
        <h4 style={{ fontSize: '0.85rem', fontWeight: 600, color: INK, margin: 0 }}>Sector rotation</h4>
        {ready && <span style={{ fontSize: '0.7rem', color: '#6b7280' }}>as of the {shortDate(rotation.asOf)} close</span>}
      </div>

      {!ready ? (
        <div style={{ fontSize: '0.72rem', color: '#6b7280', padding: '14px 0' }}>
          The map needs {rotation.sessionsNeeded} settled sessions; {rotation.sessionsAvailable} are stored so far.
        </div>
      ) : (
        <>
          <SectorPinPills sectors={sectors} pins={pins} />
          <div ref={containerRef} style={{ width: '100%' }}>
            {width > 0 && <Plot points={rotation.points} width={width} colorFor={colorFor} hovered={hovered} setHovered={setHovered} />}
          </div>
          <QuadrantTable points={rotation.points} colorFor={colorFor} hovered={hovered} setHovered={setHovered} />
          <div style={{ fontSize: '0.65rem', color: MUTED, marginTop: '8px', lineHeight: 1.5 }}>
            Right = the sector's {RS_WINDOW}-session return is ahead of the 11-sector average; up = that lead grew over the last{' '}
            {MOMENTUM_WINDOW} sessions. The line runs from where the sector stood {TRAIL_LENGTH - 1} sessions ago to the dot; pin or hover a sector for its day-by-day path. Values are percentage points:
            lead (change). Settled closes, cap-weighted. This describes where sectors stand — in 27 years of sector data,
            position on this map did not predict the following days' returns.
          </div>
        </>
      )}
    </div>
  );
};

export default SectorRotationMap;
